import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChefHat, MessageSquare, Sparkles, Rss, ShoppingCart } from 'lucide-react';

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastScrollTime = useRef(0);
  const [hasUnreadFeed, setHasUnreadFeed] = useState(false);
  const [groceryCount, setGroceryCount] = useState(0);
  
  // Check for unread feed items
  useEffect(() => {
    const checkUnreadFeed = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/feed/unread-count`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setHasUnreadFeed(data.has_unread || false);
        }
      } catch (error) {
        // Silently fail - not critical
      }
    };
    
    checkUnreadFeed();
    // Check periodically
    const interval = setInterval(checkUnreadFeed, 60000); // Every minute
    return () => clearInterval(interval);
  }, []);

  // Check for grocery count
  useEffect(() => {
    const checkGroceryCount = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${window.location.origin}/api/groceries`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          setGroceryCount(data.items?.length || 0);
        }
      } catch (error) {
        // Silently fail - not critical
      }
    };
    
    checkGroceryCount();
    // Check periodically
    const interval = setInterval(checkGroceryCount, 30000); // Every 30 seconds
    
    // Also listen for custom event when groceries change
    const handleGroceriesChange = () => checkGroceryCount();
    window.addEventListener('groceries-changed', handleGroceriesChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('groceries-changed', handleGroceriesChange);
    };
  }, []);
  
  // Clear unread when visiting feed
  useEffect(() => {
    if (location.pathname === '/feed') {
      setHasUnreadFeed(false);
    }
  }, [location.pathname]);
  
  // Track scroll to prevent accidental taps while scrolling
  useEffect(() => {
    const handleScroll = () => {
      lastScrollTime.current = Date.now();
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
    };
  }, []);
  
  const isActive = (path) => {
    if (path === '/recipe-box') {
      return location.pathname === '/recipe-box' || location.pathname.startsWith('/recipe/');
    }
    if (path === '/chats') {
      return location.pathname === '/chats' || location.pathname === '/create';
    }
    if (path === '/buzz') {
      return location.pathname === '/buzz';
    }
    if (path === '/feed') {
      return location.pathname === '/feed';
    }
    if (path === '/groceries') {
      return location.pathname === '/groceries';
    }
    return location.pathname === path;
  };

  const navItems = [
    { path: '/recipe-box', label: 'Recipes', icon: ChefHat },
    { path: '/chats', label: 'AI Chat', icon: MessageSquare },
    { path: '/buzz', label: 'My Buzz', icon: Sparkles, emoji: '🐝' },
    { path: '/feed', label: 'Feed', icon: Rss, emoji: '🍯' },
    { path: '/groceries', label: 'Groceries', icon: ShoppingCart, showCount: true },
  ];
  
  const handleNavClick = useCallback((path) => {
    // Prevent accidental navigation if user was scrolling recently (within 200ms)
    const timeSinceScroll = Date.now() - lastScrollTime.current;
    if (timeSinceScroll < 200) {
      return; // Ignore tap - user was scrolling
    }
    navigate(path);
  }, [navigate]);

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-amber-200 z-50 safe-area-bottom">
      <div className="max-w-lg mx-auto flex justify-around items-center h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const showFeedBadge = item.path === '/feed' && hasUnreadFeed && !active;
          const showGroceryCount = item.showCount && groceryCount > 0;
          return (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={`relative flex flex-col items-center justify-center w-full h-full transition-colors ${
                active 
                  ? 'text-amber-600' 
                  : 'text-gray-500 hover:text-amber-600'
              }`}
            >
              {showFeedBadge && (
                <span className="absolute top-2 right-1/4 w-2.5 h-2.5 bg-blue-500 rounded-full" />
              )}
              {showGroceryCount && (
                <span className="absolute top-1 right-1/4 min-w-[18px] h-[18px] px-1 bg-amber-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {groceryCount > 99 ? '99+' : groceryCount}
                </span>
              )}
              {item.emoji ? (
                <span className={`text-2xl ${active ? 'scale-110' : ''} transition-transform`}>{item.emoji}</span>
              ) : (
                <Icon size={24} strokeWidth={active ? 2.5 : 2} />
              )}
              <span className={`text-xs mt-1 ${active ? 'font-semibold' : ''}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;